import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  MessageCircle, X, Send, Bot, User, FileText, 
  UserSearch, Network, CheckSquare, Sparkles, Loader2,
  ChevronDown, Minimize2, Maximize2
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KnowledgeAssistant = () => {
  const { token, user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversation, setConversation] = useState([]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conversation]);

  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  const handleSend = async () => {
    if (!message.trim() || loading) return;

    const userMessage = message.trim();
    setMessage('');
    
    // Add user message to conversation
    setConversation(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/assistant/chat`, {
        message: userMessage,
        conversation_history: conversation.slice(-6)
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      // Add assistant response
      setConversation(prev => [...prev, {
        role: 'assistant',
        content: response.data.response,
        sources: response.data.sources,
        suggestions: response.data.suggestions
      }]);
    } catch (error) {
      console.error('Assistant error:', error);
      setConversation(prev => [...prev, {
        role: 'assistant',
        content: "I'm sorry, I encountered an error. Please try again.",
        sources: [],
        suggestions: ['Try again', 'Browse knowledge base']
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setMessage(suggestion);
    inputRef.current?.focus();
  };

  const getSourceIcon = (type) => {
    switch (type) {
      case 'article': return <FileText className="w-3 h-3" />;
      case 'expert': return <UserSearch className="w-3 h-3" />;
      case 'knowledge_map': return <Network className="w-3 h-3" />;
      case 'task': return <CheckSquare className="w-3 h-3" />;
      default: return <FileText className="w-3 h-3" />;
    }
  };

  const getSourceLink = (source) => {
    switch (source.type) {
      case 'article': return '/workspace';
      case 'expert': return '/expertise';
      case 'knowledge_map': return '/knowledge-maps';
      case 'task': return '/tasks';
      default: return '/search';
    }
  };

  const getSourceColor = (type) => {
    switch (type) {
      case 'article': return 'bg-[#002FA7]/10 text-[#002FA7]';
      case 'expert': return 'bg-[#AF52DE]/10 text-[#AF52DE]';
      case 'knowledge_map': return 'bg-[#00C7BE]/10 text-[#00C7BE]';
      case 'task': return 'bg-[#34C759]/10 text-[#34C759]';
      default: return 'bg-[#9CA3AF]/10 text-[#9CA3AF]';
    }
  };

  if (!user) return null;

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-[#002FA7] text-white rounded-full shadow-lg hover:bg-[#00227A] transition-all hover:scale-105 flex items-center justify-center"
          style={{ zIndex: 9999 }}
          data-testid="assistant-toggle-btn"
          type="button"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#34C759] rounded-full flex items-center justify-center">
            <Sparkles className="w-2.5 h-2.5" />
          </span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className={`fixed bottom-6 right-6 w-[400px] bg-white rounded-lg shadow-2xl border border-[#E5E7EB] flex flex-col transition-all ${
            isMinimized ? 'h-14' : 'h-[600px] max-h-[80vh]'
          }`}
          style={{ zIndex: 9999 }}
          data-testid="assistant-window"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB] bg-[#002FA7] text-white rounded-t-lg">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Knowledge Assistant</h3>
                <span className="text-xs text-white/70">AI-powered help</span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button 
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-1.5 hover:bg-white/20 rounded transition-colors"
              >
                {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
              </button>
              <button 
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-white/20 rounded transition-colors"
                data-testid="assistant-close-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Messages */}
              <ScrollArea className="flex-1 p-4" ref={scrollRef}>
                {conversation.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-[#002FA7]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Sparkles className="w-8 h-8 text-[#002FA7]" />
                    </div>
                    <h4 className="font-semibold text-[#0A0A0A] mb-2">How can I help?</h4>
                    <p className="text-sm text-[#4B5563] mb-6">
                      Ask me anything about your knowledge base, find experts, or discover articles.
                    </p>
                    <div className="space-y-2">
                      {['Find articles about project management', 'Who is an expert in Python?', 'Show me knowledge maps'].map((q, i) => (
                        <button
                          key={i}
                          onClick={() => { setMessage(q); inputRef.current?.focus(); }}
                          className="w-full text-left px-3 py-2 text-sm bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded transition-colors"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {conversation.map((msg, idx) => (
                      <div key={idx} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                        {msg.role === 'assistant' && (
                          <div className="w-8 h-8 bg-[#002FA7] rounded-full flex items-center justify-center flex-shrink-0">
                            <Bot className="w-4 h-4 text-white" />
                          </div>
                        )}
                        <div className={`max-w-[85%] ${msg.role === 'user' ? 'order-first' : ''}`}>
                          <div className={`p-3 rounded-lg ${
                            msg.role === 'user' 
                              ? 'bg-[#002FA7] text-white rounded-br-none' 
                              : 'bg-[#F3F4F6] text-[#0A0A0A] rounded-bl-none'
                          }`}>
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          </div>
                          
                          {/* Sources */}
                          {msg.sources?.length > 0 && (
                            <div className="mt-2 space-y-1">
                              <span className="text-xs text-[#9CA3AF]">Sources:</span>
                              <div className="flex flex-wrap gap-1">
                                {msg.sources.slice(0, 4).map((source, sIdx) => (
                                  <Link 
                                    key={sIdx} 
                                    to={getSourceLink(source)}
                                    className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded ${getSourceColor(source.type)} hover:opacity-80`}
                                  >
                                    {getSourceIcon(source.type)}
                                    <span className="truncate max-w-[120px]">{source.title}</span>
                                  </Link>
                                ))}
                                {msg.sources.length > 4 && (
                                  <span className="text-xs text-[#9CA3AF] px-2 py-1">
                                    +{msg.sources.length - 4} more
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Suggestions */}
                          {msg.suggestions?.length > 0 && idx === conversation.length - 1 && msg.role === 'assistant' && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {msg.suggestions.map((sug, sIdx) => (
                                <button
                                  key={sIdx}
                                  onClick={() => handleSuggestionClick(sug)}
                                  className="text-xs px-2 py-1 bg-white border border-[#E5E7EB] rounded hover:border-[#002FA7] hover:text-[#002FA7] transition-colors"
                                >
                                  {sug}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {msg.role === 'user' && (
                          <div className="w-8 h-8 bg-[#4B5563] rounded-full flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {/* Loading indicator */}
                    {loading && (
                      <div className="flex gap-2">
                        <div className="w-8 h-8 bg-[#002FA7] rounded-full flex items-center justify-center">
                          <Bot className="w-4 h-4 text-white" />
                        </div>
                        <div className="bg-[#F3F4F6] p-3 rounded-lg rounded-bl-none">
                          <div className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin text-[#002FA7]" />
                            <span className="text-sm text-[#4B5563]">Searching knowledge base...</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </ScrollArea>

              {/* Input */}
              <div className="p-4 border-t border-[#E5E7EB]">
                <div className="flex gap-2">
                  <Input
                    ref={inputRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask about articles, experts, or tasks..."
                    className="flex-1"
                    disabled={loading}
                    data-testid="assistant-input"
                  />
                  <Button 
                    onClick={handleSend}
                    disabled={!message.trim() || loading}
                    className="bg-[#002FA7] hover:bg-[#00227A]"
                    data-testid="assistant-send-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-[#9CA3AF] mt-2 text-center">
                  Powered by GPT-5.2 • Searches articles, experts & knowledge maps
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default KnowledgeAssistant;
